/**
 * venture/terms.tsx — 조합 조건 구분(조합 · LP 후보 공용) · 2026-10-28 지도사
 *  ① 이체 구분 「개인투자조합 통장에 돈 입금 후 법인통장에 바로 이체할 조합과 2주 대기할 조합 구분 · LP 매칭할 때 참고 · LP 대기 리스트에도 동일하게」
 *     now = 바로 이체 · wait2w = 2주 대기
 *  ② 사채 처리 「전환사채 발행한 것에 대해서 소득공제 신청인지 바로 회수인지도 추가 표시 · 사채 소득공제 신청 / 사채 바로 회수 두 개 · LP 정보에도」
 *     deduct = 사채 소득공제 신청 · redeem = 사채 바로 회수
 *  '' = 미정 · 조합: data.<key>(등록정보) · LP 후보: venture_lp_pool.<key>(그 LP 가 원하는 쪽) — 매칭할 때 다르면 알려 준다(막지는 않음)
 *  배지는 두 칸 「이체 │ 2주 대기」 「사채 │ 소득공제 신청」 — 무슨 구분인지 앞 칸이 말해 준다(2026-10-28 지도사 「명확히 구분되게 직관적으로」)
 *  구분을 하나 더 늘릴 때는 AXES 에 한 줄 + 서버 routes/venture.js LP_TERMS + 마이그레이션
 */
import React, { useState } from 'react';

export type Withdraw = 'now' | 'wait2w' | '';
export type Bond = 'deduct' | 'redeem' | '';
export type TermKey = 'withdraw' | 'bond';
export interface TermOpt { v: string; label: string; short: string; pill: string; desc: string; cls: string }
export interface Axis { key: TermKey; name: string; tag: string; all: string; unset: string; unsetTitle: string; opts: TermOpt[] }

export const WITHDRAW_AXIS: Axis = {
  key: 'withdraw', name: '이체 구분', tag: '이체', all: '이체 전체', unset: '이체 미정', unsetTitle: '조합 통장 입금 뒤 법인 통장으로 보내는 시기를 아직 안 정했어요',
  opts: [
    { v: 'now', label: '바로 이체', short: '바로 이체', pill: '바로', desc: '조합 통장 입금 뒤 법인 통장으로 바로 보냄', cls: 'tm-now' },
    { v: 'wait2w', label: '2주 대기', short: '2주 대기', pill: '2주 대기', desc: '조합 통장 입금 뒤 2주 기다렸다 법인 통장으로 보냄', cls: 'tm-wait' },
  ],
};
export const BOND_AXIS: Axis = {
  key: 'bond', name: '사채 처리', tag: '사채', all: '사채 전체', unset: '사채 미정', unsetTitle: '전환사채를 소득공제 신청할지 바로 회수할지 아직 안 정했어요',
  opts: [
    { v: 'deduct', label: '사채 소득공제 신청', short: '소득공제 신청', pill: '소득공제 신청', desc: '전환사채 투자금액으로 소득공제를 신청함', cls: 'tm-deduct' },
    { v: 'redeem', label: '사채 바로 회수', short: '바로 회수', pill: '바로 회수', desc: '전환사채를 바로 회수함', cls: 'tm-redeem' },
  ],
};
export const AXES: Axis[] = [WITHDRAW_AXIS, BOND_AXIS];

/** 저장 값 → 아는 값 | '' (모르는 값은 미정) */
export function termOf(axis: Axis, x: Partial<Record<TermKey, unknown>> | null | undefined): string {
  const v = x ? x[axis.key] : undefined;
  return typeof v === 'string' && axis.opts.some((o) => o.v === v) ? v : '';
}
export const withdrawOf = (x: Partial<Record<TermKey, unknown>> | null | undefined): Withdraw => termOf(WITHDRAW_AXIS, x) as Withdraw;
export const bondOf = (x: Partial<Record<TermKey, unknown>> | null | undefined): Bond => termOf(BOND_AXIS, x) as Bond;
export function termLabel(axis: Axis, v: string): string { const o = axis.opts.find((x) => x.v === v); return o ? o.label : '미정'; }

/** 정해진 구분 이름들 — 「바로 이체 · 사채 바로 회수」(조합 고르기 줄 등) */
export function termsText(x: Partial<Record<TermKey, unknown>> | null | undefined): string {
  return AXES.map((a) => termOf(a, x)).map((v, i) => (v ? termLabel(AXES[i], v) : '')).filter(Boolean).join(' · ');
}
/** LP 와 조합이 다른 구분 — 둘 다 정해져 있을 때만 */
export function termMismatches(lp: Partial<Record<TermKey, unknown>>, project: Partial<Record<TermKey, unknown>>): { axis: Axis; lp: string; project: string }[] {
  return AXES.map((axis) => ({ axis, lp: termOf(axis, lp), project: termOf(axis, project) })).filter((m) => m.lp && m.project && m.lp !== m.project);
}

/** 배지 — 두 칸 「구분 │ 값」 · 미정이면 안 그림(showUnset 이면 회색 「이체 │ 미정」) */
export function TermPill({ axis, v, showUnset }: { axis: Axis; v: string; showUnset?: boolean }) {
  const o = axis.opts.find((x) => x.v === v);
  if (!o && !showUnset) return null;
  return (
    <span className={'tm-pill ' + (o ? o.cls : 'tm-unset')} title={o ? `${axis.name} · ${o.desc}` : axis.unsetTitle} aria-label={`${axis.name} ${o ? o.label : '미정'}`}>
      <span className="tm-k">{axis.tag}</span><span className="tm-v">{o ? o.pill : '미정'}</span>
    </span>
  );
}
/** 정해진 구분 배지 모두(이체 · 사채 순) — showUnset 은 이체에만(사채는 전환사채가 없는 조합도 있어 미정을 띄우지 않음) */
export function TermPills({ x, showUnset }: { x: Partial<Record<TermKey, unknown>> | null | undefined; showUnset?: boolean }) {
  return <>{AXES.map((a) => <TermPill key={a.key} axis={a} v={termOf(a, x)} showUnset={showUnset && a.key === 'withdraw'} />)}</>;
}

/** 고르기 — 구분 둘 + 미정(같은 너비 세 칸) */
export function TermSeg({ axis, value, onChange, label }: { axis: Axis; value: string; onChange: (v: string) => void; label?: string }) {
  const opts = [...axis.opts.map((o) => ({ v: o.v, label: o.short, desc: o.desc })), { v: '', label: '미정', desc: '아직 안 정함' }];   // 칸 이름(사채 처리)이 있으니 짧게
  return (
    <div className="seg tm-seg" role="radiogroup" aria-label={label || axis.name}>
      {opts.map((o) => (
        <button key={o.v || 'none'} type="button" role="radio" aria-checked={value === o.v} className={value === o.v ? 'on' : ''} title={o.desc} onClick={() => onChange(o.v)}>{o.label}</button>
      ))}
    </div>
  );
}

/** 목록 거르기 상태 — 'all' | 구분 값 · 브라우저에 기억(보던 거르기) */
export function useTermFilter(axis: Axis, storeKey: string): [string, (v: string) => void] {
  const [v, setV] = useState<string>(() => { try { const s = localStorage.getItem(storeKey) || ''; return axis.opts.some((o) => o.v === s) ? s : 'all'; } catch { return 'all'; } });
  const pick = (x: string) => { setV(x); try { localStorage.setItem(storeKey, x); } catch { /* 화면은 동작 */ } };
  return [v, pick];
}
/** 목록 거르기 단추 — 하나라도 정해진 것이 있을 때만 보인다 */
export function TermFilter<T extends Partial<Record<TermKey, unknown>>>({ axis, items, value, onPick, className }: { axis: Axis; items: T[]; value: string; onPick: (v: string) => void; className?: string }) {
  if (!items.some((x) => termOf(axis, x)) && value === 'all') return null;
  const opts = [{ v: 'all', label: axis.all }, ...axis.opts.map((o) => ({ v: o.v, label: o.short }))];
  return (
    <div className={'seg office-filt' + (className ? ' ' + className : '')} role="radiogroup" aria-label={axis.name + '별 보기'}>
      {opts.map((o) => (
        <button key={o.v} type="button" className={value === o.v ? 'on' : ''} aria-pressed={value === o.v} onClick={() => onPick(o.v)}>
          {o.label} <span className="cnt">{o.v === 'all' ? items.length : items.filter((x) => termOf(axis, x) === o.v).length}</span>
        </button>
      ))}
    </div>
  );
}
export const passes = (axis: Axis, filter: string, x: Partial<Record<TermKey, unknown>>): boolean => filter === 'all' || termOf(axis, x) === filter;
